import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Activity, RefreshCcw, Play, AlertTriangle, CheckCircle2, XCircle,
  TrendingUp, TrendingDown, Database, Clock, Zap, Loader2, ExternalLink, Lock,
} from "lucide-react";
import { toast } from "sonner";
import { apiGet, apiPost, formatDateTime } from "@/lib/api";

// -----------------------------------------------------------------------------
// Phase 12 (2026-05-15) — Admin Report Pipeline dashboard.
//
// Surfaces the pipeline_orchestrator_service state in 4 sections:
//   1. Today's Activity  — counts + significant changes + failed list
//   2. Pipeline Status   — pending / in-progress / last run / next run
//   3. Coverage          — recent vs old vs no-data buckets
//   4. Recent Changes    — last 20 stock updates with EPS delta + signal flip
//
// The Run Now button kicks off the full download + auto-analyse pipeline
// inline; expect a multi-minute wait when the queue is large. A spinner
// covers the entire page while the request is in-flight.
// -----------------------------------------------------------------------------

const fmtNum = (v) => {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return "—";
  return Number(v).toLocaleString();
};

const fmtEps = (v) => {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return "—";
  return Number(v).toFixed(2);
};

const fmtPct = (v) => {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return "—";
  const n = Number(v);
  const sign = n > 0 ? "+" : "";
  return `${sign}${n.toFixed(1)}%`;
};

const SignalBadge = ({ signal }) => {
  if (!signal) return <span className="text-muted-foreground text-xs">—</span>;
  const tone = {
    STRONG_BUY: "bg-emerald-100 text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-200",
    BUY: "bg-emerald-50 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300",
    WATCH: "bg-sky-50 text-sky-800 dark:bg-sky-900/30 dark:text-sky-300",
    HOLD: "bg-zinc-100 text-zinc-700 dark:bg-zinc-800/60 dark:text-zinc-300",
    AVOID: "bg-amber-50 text-amber-900 dark:bg-amber-900/30 dark:text-amber-200",
    STRONG_AVOID: "bg-red-50 text-red-900 dark:bg-red-900/30 dark:text-red-200",
  }[signal] || "bg-zinc-100 text-zinc-700";
  return (
    <span
      data-testid={`signal-${signal.toLowerCase()}`}
      className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold ${tone}`}
    >
      {signal.replace("_", " ")}
    </span>
  );
};

const StatTile = ({ label, value, icon: Icon, tone = "default", testId }) => {
  const tones = {
    default: "border-border bg-card",
    success: "border-emerald-300/60 bg-emerald-50 dark:bg-emerald-950/30",
    warning: "border-amber-300/60 bg-amber-50 dark:bg-amber-950/30",
    danger: "border-red-300/60 bg-red-50 dark:bg-red-950/30",
    info: "border-sky-300/60 bg-sky-50 dark:bg-sky-950/30",
  };
  return (
    <div
      data-testid={testId}
      className={`flex items-center gap-3 rounded-lg border p-3 ${tones[tone]}`}
    >
      {Icon && <Icon className="h-4 w-4 text-muted-foreground" />}
      <div className="flex-1">
        <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
          {label}
        </div>
        <div className="text-xl font-semibold leading-tight">{value}</div>
      </div>
    </div>
  );
};

// v12.4 — Live banner shown at the very top of the dashboard. Renders
// in three states:
//   1. RUNNING   — yellow card, animated spinner, progress bar with
//                  "Processing X of Y · currently TICKER".
//   2. LAST RUN  — green card with the most recent completed-run counts
//                  (analysed / failed / significant).
//   3. NEVER RUN — neutral card with a hint to click Run Now.
const LivePipelineBanner = ({ isRunning, progress, runNowState, lastRun }) => {
  if (isRunning) {
    const processed = Number(progress?.processed_count ?? 0);
    const total = Math.max(Number(progress?.total_queued ?? 0), processed);
    const pct = total > 0 ? Math.min(100, Math.round((processed / total) * 100)) : 0;
    const started = progress?.started_at || runNowState?.started_at;
    const trig = progress?.triggered_by || runNowState?.triggered_by || "scheduler";
    const current = progress?.current_stock;
    // v12.5 — "Discovering" phase: total_queued=0 means the orchestrator
    // is still building the candidate queue via cse_reports_service
    // (CSE HTTP fan-out, can take 30 s – 3 min). Show an indeterminate
    // "Checking for new reports…" message instead of the misleading
    // "0 of ~?" counter that confused the admin in v12.4.
    const isDiscovering = total === 0 && processed === 0;
    return (
      <Card
        className="border-2 border-amber-400 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-500/60"
        data-testid="live-pipeline-banner-running"
      >
        <CardContent className="py-4">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <div className="flex items-center gap-2">
              <Loader2
                className="h-5 w-5 text-amber-700 dark:text-amber-300 animate-spin"
                data-testid="banner-running-spinner"
              />
              <span className="font-semibold text-amber-900 dark:text-amber-100">
                {isDiscovering ? "Checking for new reports…" : "Pipeline Running…"}
              </span>
              <Badge
                variant="outline"
                className="border-amber-500/60 bg-amber-100/60 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200"
              >
                {trig}
              </Badge>
            </div>
            <div className="text-xs text-amber-900/80 dark:text-amber-100/80">
              Started: <span data-testid="banner-started-at">{formatDateTime(started)}</span>
            </div>
          </div>
          {isDiscovering ? (
            // Indeterminate striped bar — no false progress percentage.
            <div
              className="h-2 w-full rounded bg-amber-200 dark:bg-amber-900/60 overflow-hidden relative"
              data-testid="banner-progress-bar"
            >
              <div className="absolute inset-y-0 left-0 w-1/3 bg-amber-500/70 dark:bg-amber-400/70 animate-pulse rounded" />
            </div>
          ) : (
            <Progress
              value={pct}
              className="h-2 bg-amber-200 dark:bg-amber-900/60"
              data-testid="banner-progress-bar"
            />
          )}
          <div className="flex flex-wrap items-center justify-between gap-2 mt-2 text-sm">
            <div className="text-amber-900 dark:text-amber-100" data-testid="banner-progress-counter">
              {isDiscovering ? (
                <>
                  Scanning the Colombo Stock Exchange for new annual &amp; interim reports…
                  This usually takes <strong>30 s – 3 min</strong>.
                </>
              ) : (
                <>
                  Processing <strong>{processed}</strong> of <strong>{total}</strong> reports
                  {current && (
                    <>
                      &nbsp;· currently analysing&nbsp;
                      <code
                        className="px-1.5 py-0.5 rounded bg-amber-200/80 dark:bg-amber-900/60 text-amber-900 dark:text-amber-100 text-[12px] font-semibold"
                        data-testid="banner-current-stock"
                      >
                        {current}
                      </code>
                    </>
                  )}
                </>
              )}
            </div>
            <div className="text-xs text-amber-900/70 dark:text-amber-100/70">
              {isDiscovering ? "auto-refreshes every 10s" : `${pct}% complete · auto-refreshes every 10s`}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // ────────── IDLE: show the last completed run ──────────
  if (lastRun?.completed_at) {
    const analysed = lastRun.analysed ?? 0;
    const failed = lastRun.failed ?? 0;
    const sig = lastRun.significant ?? 0;
    const splits = lastRun.splits ?? 0;
    const attempted = lastRun.attempted ?? 0;
    // v12.5 — When the most recent run had nothing to do, surface that
    // explicitly. Otherwise admins see "Analysed: 0 · Failed: 0" and
    // mistake it for a broken pipeline.
    const wasEmpty = attempted === 0;
    return (
      <Card
        className="border-2 border-emerald-300 bg-emerald-50 dark:bg-emerald-950/30 dark:border-emerald-600/60"
        data-testid="live-pipeline-banner-idle"
      >
        <CardContent className="py-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
              <span className="font-semibold text-emerald-900 dark:text-emerald-100">
                Last run complete
              </span>
              <Badge
                variant="outline"
                className="border-emerald-500/60 bg-emerald-100/60 text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-200"
              >
                {lastRun.triggered_by || "scheduler"}
              </Badge>
              {wasEmpty && (
                <Badge
                  variant="outline"
                  className="border-sky-400/60 bg-sky-100/60 text-sky-900 dark:bg-sky-900/40 dark:text-sky-200"
                  data-testid="banner-last-run-empty-badge"
                >
                  no new reports
                </Badge>
              )}
            </div>
            <div className="text-xs text-emerald-900/80 dark:text-emerald-100/80">
              <span data-testid="banner-last-completed-at">
                {formatDateTime(lastRun.completed_at)}
              </span>
            </div>
          </div>
          <div className="mt-2 flex flex-wrap gap-3 text-sm">
            <span data-testid="banner-stat-attempted">
              <strong className="text-emerald-700 dark:text-emerald-300">Attempted:</strong> {attempted}
            </span>
            <span data-testid="banner-stat-analysed">
              <strong className="text-emerald-700 dark:text-emerald-300">Analysed:</strong> {analysed}
            </span>
            <span data-testid="banner-stat-failed">
              <strong className={failed > 0 ? "text-red-700 dark:text-red-300" : "text-emerald-700 dark:text-emerald-300"}>
                Failed (this run):
              </strong> {failed}
            </span>
            <span data-testid="banner-stat-significant">
              <strong className={sig > 0 ? "text-amber-700 dark:text-amber-300" : "text-emerald-700 dark:text-emerald-300"}>
                Significant:
              </strong> {sig}
            </span>
            <span data-testid="banner-stat-splits">
              <strong className="text-emerald-700 dark:text-emerald-300">Splits:</strong> {splits}
            </span>
          </div>
        </CardContent>
      </Card>
    );
  }

  // ────────── No runs yet ──────────
  return (
    <Card data-testid="live-pipeline-banner-empty" className="border-dashed">
      <CardContent className="py-4 text-sm text-muted-foreground italic">
        No pipeline runs yet. Click <strong>Run Now</strong> above to kick off the first pass,
        or wait for the daily 18:30 SLT scheduler.
      </CardContent>
    </Card>
  );
};



// Phase 12.6 (2026-05-15) — Backfill coverage block rendered inside
// the Data Coverage section. Shows the live "stocks still needing
// reports" counts with a sample list AND the last completed backfill
// run summary so admins know whether the daily ticks are working.
const BackfillCoverageBlock = ({ backfill, backfillState, onRunBackfill, starting }) => {
  const annualCount = backfill?.missing_recent_annual?.count ?? 0;
  const annualSample = backfill?.missing_recent_annual?.sample ?? [];
  const years = backfill?.missing_recent_annual?.years_considered ?? [];
  const interimCount = backfill?.missing_recent_interim?.count ?? 0;
  const interimSample = backfill?.missing_recent_interim?.sample ?? [];
  const cutoff = backfill?.missing_recent_interim?.cutoff;
  const isRunning = !!backfillState?.running;
  const lastSummary = backfillState?.last_summary;

  // Friendly cutoff date for the interim subtitle.
  const cutoffStr = cutoff
    ? new Date(cutoff).toLocaleDateString(undefined, { year: "numeric", month: "short" })
    : "—";

  return (
    <div data-testid="coverage-backfill-block" className="space-y-3">
      <div className="flex flex-wrap items-end justify-between gap-2 mt-2">
        <div>
          <div className="text-xs font-medium text-muted-foreground">
            Backfill needed
          </div>
          <div className="text-[11px] text-muted-foreground/80">
            Stocks still missing their latest annual or post-{cutoffStr} interim filings.
            The scheduler downloads <strong>50 stocks per day</strong> until the backlog clears.
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={onRunBackfill}
          disabled={starting || isRunning || (annualCount + interimCount === 0)}
          className="border-indigo-400 text-indigo-700 hover:bg-indigo-50 dark:text-indigo-300 dark:hover:bg-indigo-950/40"
          data-testid="coverage-backfill-run-btn"
        >
          {starting ? (
            <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Starting…</>
          ) : isRunning ? (
            <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Backfilling…</>
          ) : (
            <><Play className="h-4 w-4 mr-1" /> Run Backfill Now</>
          )}
        </Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div
          className={`rounded-lg border p-3 ${
            annualCount > 0
              ? "border-amber-300/60 bg-amber-50 dark:bg-amber-950/30"
              : "border-emerald-300/60 bg-emerald-50 dark:bg-emerald-950/30"
          }`}
          data-testid="coverage-backfill-annual"
        >
          <div className="flex items-center justify-between">
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
              Missing latest annual
            </div>
            <span
              className="text-[11px] text-muted-foreground/80"
              title={`Counts as covered if any annual row exists for ${years.join(" / ")}`}
            >
              {years.join(" / ")}
            </span>
          </div>
          <div
            className="text-xl font-semibold leading-tight"
            data-testid="coverage-backfill-annual-count"
          >
            {fmtNum(annualCount)}
          </div>
          {annualSample.length > 0 && (
            <div className="mt-1 flex flex-wrap gap-1">
              {annualSample.slice(0, 10).map((code) => (
                <Link key={code} to={`/stocks/${code}`}>
                  <Badge
                    variant="outline"
                    className="text-[10px] hover:bg-muted"
                  >
                    {code}
                  </Badge>
                </Link>
              ))}
              {annualCount > annualSample.length && (
                <span className="text-[10px] text-muted-foreground self-center">
                  …+{annualCount - annualSample.length} more
                </span>
              )}
            </div>
          )}
        </div>

        <div
          className={`rounded-lg border p-3 ${
            interimCount > 0
              ? "border-amber-300/60 bg-amber-50 dark:bg-amber-950/30"
              : "border-emerald-300/60 bg-emerald-50 dark:bg-emerald-950/30"
          }`}
          data-testid="coverage-backfill-interim"
        >
          <div className="flex items-center justify-between">
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
              Missing post-{cutoffStr} interim
            </div>
          </div>
          <div
            className="text-xl font-semibold leading-tight"
            data-testid="coverage-backfill-interim-count"
          >
            {fmtNum(interimCount)}
          </div>
          {interimSample.length > 0 && (
            <div className="mt-1 flex flex-wrap gap-1">
              {interimSample.slice(0, 10).map((code) => (
                <Link key={code} to={`/stocks/${code}`}>
                  <Badge
                    variant="outline"
                    className="text-[10px] hover:bg-muted"
                  >
                    {code}
                  </Badge>
                </Link>
              ))}
              {interimCount > interimSample.length && (
                <span className="text-[10px] text-muted-foreground self-center">
                  …+{interimCount - interimSample.length} more
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Last completed backfill run summary */}
      {lastSummary && (
        <div
          className="text-[12px] text-muted-foreground border-t pt-2 mt-1"
          data-testid="coverage-backfill-last-summary"
        >
          <strong>Last backfill:</strong>&nbsp;
          annual <code className="px-1 rounded bg-muted text-foreground/80">{lastSummary.annual_downloaded ?? 0}</code> downloaded
          of <code className="px-1 rounded bg-muted text-foreground/80">{lastSummary.annual_attempted ?? 0}</code> attempted,
          &nbsp;interim <code className="px-1 rounded bg-muted text-foreground/80">{lastSummary.interim_downloaded ?? 0}</code> downloaded
          of <code className="px-1 rounded bg-muted text-foreground/80">{lastSummary.interim_attempted ?? 0}</code> attempted
          {typeof lastSummary.elapsed_s === "number" && (
            <> · {lastSummary.elapsed_s.toFixed(1)}s</>
          )}
        </div>
      )}
    </div>
  );
};



const AdminReportPipelinePage = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  // Phase 12.6 — separate "Run Backfill Now" button state, independent
  // of the orchestrator Run Now flow. Admins can legitimately want to
  // backfill while an OCR run is winding down.
  const [backfillStarting, setBackfillStarting] = useState(false);
  // v12.4 — Track the previous "is running" so we can fire a toast
  // exactly once on the running → idle transition (works for both
  // admin Run Now AND scheduler runs that happen while the admin
  // happens to have the dashboard open).
  const prevIsRunningRef = useRef(false);

  const fetchStatus = useCallback(async () => {
    try {
      const data = await apiGet("/admin/report-pipeline/status");
      setStatus(data);
    } catch (e) {
      toast.error("Failed to load pipeline status", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setLoading(false);
    }
  }, []);

  // v5 (2026-05-16) — Failed-sanity reports for the admin review section.
  // These are reports whose OCR extraction was rejected by the PE /
  // negative-EPS sanity gate. Listed separately so admins can locate
  // them quickly and apply a manual override via Data Quality.
  const [failedSanity, setFailedSanity] = useState({ rows: [], total: 0 });
  const fetchFailedSanity = useCallback(async () => {
    try {
      const data = await apiGet("/admin/reports/failed-sanity?limit=50");
      setFailedSanity({ rows: data?.rows || [], total: data?.total || 0 });
    } catch (e) {
      // Non-fatal: section just renders empty.
      console.warn("[AdminReportPipeline] failed-sanity fetch failed", e);
    }
  }, []);

  // v12.9.8 (2026-05-17) — Ghost-PDF preview. Counts company_reports
  // rows whose `file_path` is set in DB but the PDF is missing on disk.
  // Surfaced in the Coverage section + the Force Backfill confirm dialog
  // so admins know exactly what's about to be re-downloaded.
  const [ghostPreview, setGhostPreview] = useState({ ghost_count: 0, sample_codes: [] });
  const fetchGhostPreview = useCallback(async () => {
    try {
      const data = await apiGet("/admin/reports/ghost-pdfs/preview");
      setGhostPreview(data || { ghost_count: 0, sample_codes: [] });
    } catch (e) {
      console.warn("[AdminReportPipeline] ghost preview fetch failed", e);
    }
  }, []);

  // v13.0 (2026-05-18) — Title-based FY remediation state.
  //   • recomputePreview: result of dry-run /admin/reports/recompute-periods.
  //   • cseNoAnnual: result of /admin/reports/cse-no-annual (cached 1h).
  const [recomputePreview, setRecomputePreview] = useState(null);
  const [recomputeRunning, setRecomputeRunning] = useState(false);
  const [cseNoAnnual, setCseNoAnnual] = useState(null);
  const [cseNoAnnualLoading, setCseNoAnnualLoading] = useState(false);

  // v13.6 (2026-05-18) — Master Claude API kill-switch.
  // `claudeStatus` mirrors GET /admin/settings/claude-status:
  //   { enabled, db_flag, env_override, forced_off_by_env,
  //     forced_on_by_env, last_toggled_at, last_toggled_by }
  const [claudeStatus, setClaudeStatus] = useState(null);
  const [claudeToggling, setClaudeToggling] = useState(false);

  const fetchClaudeStatus = useCallback(async () => {
    try {
      const s = await apiGet("/admin/settings/claude-status");
      setClaudeStatus(s);
    } catch (e) {
      console.warn("[AdminReportPipeline] claude-status fetch failed", e);
    }
  }, []);

  const handleClaudeToggle = useCallback(async () => {
    if (!claudeStatus) return;
    const target = !claudeStatus.enabled;
    const confirmMsg = target
      ? "ENABLE Claude API? This immediately allows OCR, Deep Analysis, chat, rationale, sector context, and every other AI feature to spend Anthropic credits."
      : "DISABLE Claude API? This immediately blocks EVERY AI feature. Manual and scheduled runs will error until re-enabled.";
    if (!window.confirm(confirmMsg)) return;
    setClaudeToggling(true);
    try {
      const res = await apiPost(`/admin/settings/claude-toggle?enabled=${target}`, {});
      setClaudeStatus(res);
      toast.success(target ? "Claude API ENABLED ⚠️" : "Claude API DISABLED ✅", {
        description: target
          ? "AI features are now live. Monitor Anthropic spend."
          : "All Claude spend is halted. Re-enable when needed.",
      });
    } catch (e) {
      toast.error("Toggle failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setClaudeToggling(false);
    }
  }, [claudeStatus]);

  const fetchRecomputePreview = useCallback(async () => {
    setRecomputeRunning(true);
    try {
      const data = await apiPost(
        "/admin/reports/recompute-periods?dry_run=true&report_types=annual",
        {},
      );
      setRecomputePreview(data);
    } catch (e) {
      toast.error("Could not load recompute preview", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setRecomputeRunning(false);
    }
  }, []);

  const handleApplyRecompute = useCallback(async () => {
    if (!recomputePreview || recomputePreview.would_update === 0) return;
    if (!window.confirm(
      `Apply title-derived period labels to ${recomputePreview.would_update} ` +
      "rows?\n\n" +
      "Each row's previous period is preserved as `previous_report_period` " +
      "so you can roll back if needed."
    )) return;
    setRecomputeRunning(true);
    try {
      const data = await apiPost(
        "/admin/reports/recompute-periods?dry_run=false&report_types=annual",
        {},
      );
      toast.success(`Updated ${data.applied} rows`, {
        description: "Title-derived FY labels now applied. Refresh coverage to see effect.",
      });
      setRecomputePreview({ ...data, dry_run: false });
      await fetchStatus();
    } catch (e) {
      toast.error("Recompute failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setRecomputeRunning(false);
    }
  }, [recomputePreview, fetchStatus]);

  const fetchCseNoAnnual = useCallback(async (forceRefresh = false) => {
    setCseNoAnnualLoading(true);
    try {
      // 1. Kick off (or fast-resume) the background probe.
      const start = await apiPost(
        `/admin/reports/cse-no-annual/start${forceRefresh ? "?force=true" : ""}`,
        {},
      );
      setCseNoAnnual(start);
      // 2. If already done (cached), we're finished.
      if (start.status === "done" && start.result) {
        setCseNoAnnualLoading(false);
        return;
      }
      // 3. Poll every 2s until done or error. Hard cap: 3 min.
      const startedAt = Date.now();
      const poll = async () => {
        if (Date.now() - startedAt > 180_000) {
          toast.error("CSE probe timed out", {
            description: "It took longer than 3 minutes. Try again later.",
          });
          setCseNoAnnualLoading(false);
          return;
        }
        try {
          const s = await apiGet("/admin/reports/cse-no-annual");
          setCseNoAnnual(s);
          if (s.status === "done" || s.status === "error") {
            setCseNoAnnualLoading(false);
            if (s.status === "error") {
              toast.error("CSE probe failed", {
                description: s.error || "Unknown error.",
              });
            }
            return;
          }
          setTimeout(poll, 2000);
        } catch (e) {
          console.warn("[CseNoAnnual] poll error", e);
          setTimeout(poll, 3000);
        }
      };
      setTimeout(poll, 2000);
    } catch (e) {
      toast.error("Could not start CSE no-annual probe", {
        description: e?.response?.data?.detail || e.message,
      });
      setCseNoAnnualLoading(false);
    }
  }, []);


  // v12.4 — `is_running` is the canonical flag: covers BOTH the admin
  // Run Now path (run_now_state.running) AND the scheduler-driven path
  // (progress.is_running). We OR them so the banner stays yellow
  // throughout any active pipeline work.
  const progress = status?.pipeline_status?.progress ?? {};
  const runNowState = status?.pipeline_status?.run_now_state ?? {};
  // Phase 12.6 — backfill state + coverage are separate but rendered
  // in the same "Pipeline running" banner and Coverage section.
  const backfillState = status?.pipeline_status?.backfill_state ?? {};
  const isRunning = !!(progress.is_running || runNowState.running || backfillState.running);

  useEffect(() => {
    fetchStatus();
    fetchFailedSanity();
    fetchGhostPreview();
    fetchClaudeStatus();
    // Auto-refresh — faster cadence (10s) while a Run Now is in flight
    // so progress feels alive, idle cadence (30s) otherwise.
    const tick = () => {
      const interval = isRunning ? 10_000 : 30_000;
      const id = setTimeout(async () => {
        await fetchStatus();
        await fetchFailedSanity();
        await fetchGhostPreview();
        await fetchClaudeStatus();
        tick();
      }, interval);
      return id;
    };
    const id = tick();
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRunning, fetchStatus, fetchFailedSanity, fetchGhostPreview, fetchClaudeStatus]);

  // v12.4 — Fire the "Pipeline complete!" toast exactly once on the
  // running → idle transition. Uses last_summary_counts from the
  // detached background task so we can show the analysed/failed counts
  // even when the admin wasn't watching during the actual run.
  // v12.5 — When `attempted === 0` we show a friendlier "Nothing new
  // to analyse" message instead of the generic counts (which would
  // read "Analysed: 0 · Failed: 0" and look broken).
  useEffect(() => {
    const wasRunning = prevIsRunningRef.current;
    if (wasRunning && !isRunning) {
      const counts = runNowState.last_summary_counts;
      const err = runNowState.last_error;
      if (err) {
        toast.error("Pipeline run failed", {
          description: err,
          duration: 8000,
        });
      } else if (counts && (counts.attempted ?? 0) === 0) {
        toast.success("Pipeline complete", {
          description: "Nothing new to analyse. Pending: 0",
          duration: 5000,
        });
      } else if (counts) {
        const sig = counts.significant ?? 0;
        const analysed = counts.analysed ?? 0;
        const failed = counts.failed ?? 0;
        toast.success("Pipeline complete!", {
          description: `Analysed: ${analysed} · Failed: ${failed}${sig ? ` · Significant: ${sig}` : ""}`,
          duration: 6000,
        });
      } else {
        toast.success("Pipeline complete", {
          description: "Latest run finished. Dashboard refreshed.",
        });
      }
    }
    prevIsRunningRef.current = isRunning;
  }, [isRunning, runNowState.last_summary_counts, runNowState.last_error]);

  const handleRunNow = async () => {
    if (running) return;
    if (!window.confirm(
      "Run the full pipeline now?\n\n" +
      "This downloads any new reports + runs OCR + recomputes scores + " +
      "fires admin alerts. Per-run cap: 30 reports (configurable). " +
      "Runs in the background — usually takes 2–5 minutes. " +
      "Watch this dashboard for live progress."
    )) return;
    setRunning(true);
    try {
      const res = await apiPost("/admin/report-pipeline/run-now", {});
      // v12.3 — fire-and-forget. Server returns immediately with
      // status="started" (or "already_running" if a previous click
      // is still in flight). No error toast on either case.
      if (res?.status === "already_running") {
        toast.info("Pipeline already running", {
          description:
            "A previous Run Now is still working through the queue. " +
            "Watch the dashboard for live progress.",
        });
      } else {
        toast.success("Pipeline started ✅", {
          description: "Watch the dashboard for live updates.",
        });
      }
      // Refresh now to pick up the "running" indicator, then let the
      // polling interval handle subsequent updates.
      await fetchStatus();
    } catch (e) {
      toast.error("Could not start pipeline", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setRunning(false);
    }
  };

  // Phase 12.6 — Run Backfill Now: drains the entire missing-reports
  // backlog (companies without latest annual or post-Apr-2026 interim)
  // in one go, bypassing the scheduler's 50/run safety cap. Fire-and-
  // forget like Run Now; same toast + completion-detect pattern.
  const handleRunBackfill = async () => {
    if (backfillStarting) return;
    const pending = (status?.coverage?.backfill?.missing_recent_annual?.count ?? 0)
      + (status?.coverage?.backfill?.missing_recent_interim?.count ?? 0);
    if (!window.confirm(
      "Run backfill now?\n\n" +
      `${pending} stock-buckets are currently missing recent reports. ` +
      "This downloads ALL of them in one pass (no daily limit) and " +
      "queues each for OCR. Takes several minutes for large backlogs. " +
      "Watch the dashboard for live progress."
    )) return;
    setBackfillStarting(true);
    try {
      const res = await apiPost("/admin/report-pipeline/backfill-now", {});
      if (res?.status === "already_running") {
        toast.info("Backfill already running", {
          description:
            "A previous backfill is still working through the queue. " +
            "Watch the dashboard for live progress.",
        });
      } else {
        toast.success("Backfill started ✅", {
          description: "Downloading missing reports in the background. " +
            "Dashboard will update automatically.",
        });
      }
      await fetchStatus();
    } catch (e) {
      toast.error("Could not start backfill", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setBackfillStarting(false);
    }
  };

  // v12.9.8 (2026-05-17) — Phase B: Force Backfill All. Single composite
  // trigger that:
  //   1) re-downloads "ghost" annual PDFs (rows in DB whose PDF is
  //      missing on disk),
  //   2) fetches every missing FY2024/FY2025 annual report from CSE
  //      (uncapped — no 50/run scheduler limit),
  //   3) auto-queues OCR on the new PDFs so scores refresh in the same
  //      dashboard cycle.
  // Useful when an admin has noticed dozens/hundreds of missing
  // reports and wants ONE click to drain the entire backlog instead
  // of the staged daily 50/run scheduler.
  const handleForceBackfillAll = async () => {
    if (backfillStarting || backfillState.running) return;
    const annualMissing = status?.coverage?.backfill?.missing_recent_annual?.count ?? 0;
    const ghosts = ghostPreview?.ghost_count ?? "?";
    if (!window.confirm(
      "Force Backfill ALL?\n\n" +
      `• Re-download ghost PDFs (file in DB, missing on disk): ${ghosts}\n` +
      `• Fetch missing FY2024/FY2025 annuals from CSE: ${annualMissing}\n` +
      `• Auto-queue OCR + refresh scores: yes\n\n` +
      "No daily cap. Runs in the background; takes several minutes for " +
      "large backlogs. Watch the dashboard for live progress."
    )) return;
    setBackfillStarting(true);
    try {
      const res = await apiPost(
        "/admin/report-pipeline/force-backfill-all?include_interim=false&redownload_ghosts=true&analyse_after=true",
        {},
      );
      if (res?.status === "already_running") {
        toast.info("Backfill already running", {
          description:
            "A previous backfill is still in flight — refresh the " +
            "dashboard to see live progress.",
        });
      } else {
        toast.success("Force Backfill started ✅", {
          description: "Steps: ghost re-download → FY24/FY25 annual fetch → " +
            "OCR sweep. Dashboard will update automatically.",
        });
      }
      await fetchStatus();
    } catch (e) {
      toast.error("Could not start force-backfill", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setBackfillStarting(false);
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto max-w-7xl space-y-4 p-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  const today = status?.todays_activity ?? {};
  const pipeline = status?.pipeline_status ?? {};
  const coverage = status?.coverage ?? {};
  const recent = status?.recent_changes ?? [];
  const config = status?.config ?? {};

  return (
    <div className="container mx-auto max-w-7xl space-y-6 p-6" data-testid="admin-report-pipeline-page">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Activity className="h-6 w-6 text-emerald-600" />
            Report Pipeline
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Auto-OCR + score recompute pipeline. Threshold for admin alerts:
            <strong className="mx-1">{config.significant_eps_delta_pct?.toFixed(0)}%</strong>
            EPS change. Per-run cap:
            <strong className="mx-1">{config.auto_analyse_limit ?? 30}</strong>
            reports.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isRunning && (
            <Badge
              variant="outline"
              className="border-amber-400 text-amber-800 dark:text-amber-200 bg-amber-50 dark:bg-amber-950/30 animate-pulse"
              data-testid="pipeline-running-badge"
            >
              <Loader2 className="h-3 w-3 mr-1 animate-spin" />
              Pipeline running…
            </Badge>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={fetchStatus}
            disabled={loading}
            data-testid="pipeline-refresh-btn"
          >
            <RefreshCcw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <Button
            variant="outline"
            onClick={handleRunBackfill}
            disabled={backfillStarting || backfillState.running}
            className="border-indigo-400 text-indigo-700 hover:bg-indigo-50 dark:text-indigo-300 dark:hover:bg-indigo-950/40"
            data-testid="pipeline-run-backfill-btn"
          >
            {backfillStarting ? (
              <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Starting…</>
            ) : backfillState.running ? (
              <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Backfilling…</>
            ) : (
              <><Database className="h-4 w-4 mr-1" /> Run Backfill</>
            )}
          </Button>
          <Button
            variant="outline"
            onClick={handleForceBackfillAll}
            disabled={backfillStarting || backfillState.running}
            className="border-rose-400 text-rose-700 hover:bg-rose-50 dark:text-rose-300 dark:hover:bg-rose-950/40"
            data-testid="pipeline-force-backfill-all-btn"
            title={`Re-download ${ghostPreview.ghost_count ?? "?"} ghost PDFs + fetch missing FY24/FY25 annuals, no cap`}
          >
            {backfillStarting ? (
              <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Starting…</>
            ) : backfillState.running ? (
              <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Force-backfilling…</>
            ) : (
              <>
                <Database className="h-4 w-4 mr-1" />
                Force Backfill All
                {ghostPreview.ghost_count > 0 && (
                  <span className="ml-1 text-xs opacity-80">
                    ({ghostPreview.ghost_count} ghosts)
                  </span>
                )}
              </>
            )}
          </Button>
          <Button
            onClick={handleRunNow}
            disabled={running || isRunning}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="pipeline-run-now-btn"
          >
            {running ? (
              <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Starting…</>
            ) : isRunning ? (
              <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Running…</>
            ) : (
              <><Play className="h-4 w-4 mr-1" /> Run Now</>
            )}
          </Button>
        </div>
      </div>

      {/* ───────── v12.4 — Live Pipeline Status Banner ───────── */}
      <LivePipelineBanner
        isRunning={isRunning}
        progress={progress}
        runNowState={runNowState}
        lastRun={status?.pipeline_status?.last_run}
      />

      {/* ───────── v13.6 — Claude API Master Switch ─────────
          One BIG banner at the top of the dashboard so the admin ALWAYS
          knows whether Claude spend is live. Red banner + shield icon
          when DISABLED (the default, safe state); amber banner when
          ENABLED with warning tone. The toggle is admin-only; env-var
          overrides make the state read-only and explain why. */}
      {claudeStatus && (
        <Card
          data-testid="claude-master-switch"
          className={
            claudeStatus.enabled
              ? "border-2 border-amber-400 bg-amber-50/60 dark:bg-amber-950/20"
              : "border-2 border-rose-400 bg-rose-50/60 dark:bg-rose-950/20"
          }
        >
          <CardContent className="p-4 flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              {claudeStatus.enabled ? (
                <Zap className="h-8 w-8 text-amber-600 shrink-0 mt-0.5" />
              ) : (
                <Lock className="h-8 w-8 text-rose-600 shrink-0 mt-0.5" />
              )}
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="text-lg font-bold" data-testid="claude-status-label">
                    Claude OCR:{" "}
                    <span className={claudeStatus.enabled ? "text-amber-700 dark:text-amber-300" : "text-rose-700 dark:text-rose-300"}>
                      {claudeStatus.enabled ? "ENABLED" : "DISABLED"}
                    </span>
                  </span>
                  {claudeStatus.forced_off_by_env && (
                    <Badge variant="outline" className="text-[10px] border-rose-400 text-rose-700">
                      ENV override
                    </Badge>
                  )}
                  {claudeStatus.forced_on_by_env && (
                    <Badge variant="outline" className="text-[10px] border-amber-400 text-amber-700">
                      ENV override
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground max-w-2xl">
                  {claudeStatus.enabled ? (
                    <>
                      ⚠️ Every AI feature (OCR analysis, Deep Analysis, chat, rationale,
                      sector context, IPO analysis, news summarisation) can spend
                      Anthropic credits. Monitor billing dashboard.
                    </>
                  ) : (
                    <>
                      ✅ All Claude spend is halted. Run Now, scheduled crons, Deep AI
                      requests, and every LLM-backed feature will error until an admin
                      flips this switch back ON.
                    </>
                  )}
                </p>
                {claudeStatus.last_toggled_at && (
                  <p className="text-[10px] text-muted-foreground opacity-70">
                    Last toggled {new Date(claudeStatus.last_toggled_at).toLocaleString()}
                    {claudeStatus.last_toggled_by && ` by ${claudeStatus.last_toggled_by}`}
                  </p>
                )}
                {(claudeStatus.forced_off_by_env || claudeStatus.forced_on_by_env) && (
                  <p className="text-[11px] text-rose-700 dark:text-rose-300">
                    Locked by <code className="bg-rose-100 dark:bg-rose-900/40 px-1 rounded">ENABLE_CLAUDE_API</code> env var —
                    update backend/.env to change.
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                onClick={handleClaudeToggle}
                disabled={
                  claudeToggling ||
                  claudeStatus.forced_off_by_env ||
                  claudeStatus.forced_on_by_env
                }
                className={
                  claudeStatus.enabled
                    ? "bg-rose-600 hover:bg-rose-700 text-white"
                    : "bg-emerald-600 hover:bg-emerald-700 text-white"
                }
                data-testid="claude-toggle-btn"
              >
                {claudeToggling ? (
                  <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Working…</>
                ) : claudeStatus.enabled ? (
                  <><Lock className="h-4 w-4 mr-1" /> DISABLE Claude</>
                ) : (
                  <><Zap className="h-4 w-4 mr-1" /> ENABLE Claude</>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ───────── SECTION 1 — Today's Activity ───────── */}
      <Card data-testid="section-todays-activity">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Zap className="h-4 w-4 text-emerald-600" /> Today's Activity
          </CardTitle>
          <CardDescription>
            <span className="block">
              Cumulative across <strong>all runs today</strong> (UTC day; resets at midnight).
            </span>
            {/* v12.5 — Clarify that the failure / analysed counts here
                include earlier runs, NOT just the latest one. The big
                green banner above shows the per-run breakdown. */}
            <span
              className="block mt-1 text-[11px] text-muted-foreground/80"
              data-testid="todays-activity-cumulative-note"
            >
              ℹ️ Counts include all of today's runs. For the most recent run's
              numbers, see the banner at the top of the page (<strong>Last run</strong>:&nbsp;
              <code className="px-1 rounded bg-muted text-foreground/80">{pipeline?.last_run?.failed ?? 0}</code>&nbsp;failures,&nbsp;
              <code className="px-1 rounded bg-muted text-foreground/80">{pipeline?.last_run?.analysed ?? 0}</code>&nbsp;analysed).
            </span>
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <StatTile
              label="Downloaded"
              value={fmtNum(today.downloaded)}
              icon={Database}
              testId="tile-downloaded"
            />
            <StatTile
              label="Analysed"
              value={fmtNum(today.analysed)}
              icon={CheckCircle2}
              tone={today.analysed > 0 ? "success" : "default"}
              testId="tile-analysed"
            />
            <StatTile
              label="Stocks updated"
              value={fmtNum((today.stocks_updated || []).length)}
              icon={TrendingUp}
              tone={(today.stocks_updated || []).length > 0 ? "info" : "default"}
              testId="tile-stocks-updated"
            />
            <StatTile
              label="Significant changes"
              value={fmtNum((today.significant_changes || []).length)}
              icon={AlertTriangle}
              tone={(today.significant_changes || []).length > 0 ? "warning" : "default"}
              testId="tile-significant"
            />
            <StatTile
              label="Failures"
              value={fmtNum((today.failed || []).length)}
              icon={XCircle}
              tone={(today.failed || []).length > 0 ? "danger" : "default"}
              testId="tile-failed"
            />
          </div>

          {/* Stocks updated list */}
          {(today.stocks_updated || []).length > 0 && (
            <div data-testid="stocks-updated-list">
              <div className="text-xs font-medium text-muted-foreground mb-1">
                Stocks updated today
              </div>
              <div className="flex flex-wrap gap-1">
                {today.stocks_updated.map((code) => (
                  <Link key={code} to={`/stocks/${code}`}>
                    <Badge variant="outline" className="hover:bg-muted">
                      {code}
                    </Badge>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Significant changes */}
          {(today.significant_changes || []).length > 0 && (
            <div data-testid="significant-changes-list">
              <div className="text-xs font-medium text-muted-foreground mb-2">
                Significant changes (alert fired)
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Company</TableHead>
                    <TableHead className="text-right">Old EPS</TableHead>
                    <TableHead className="text-right">New EPS</TableHead>
                    <TableHead className="text-right">Δ%</TableHead>
                    <TableHead>Signal</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {today.significant_changes.map((c, i) => (
                    <TableRow key={i}>
                      <TableCell className="font-medium">
                        <Link to={`/stocks/${c.company_code}`} className="hover:underline">
                          {c.company_code}
                        </Link>
                      </TableCell>
                      <TableCell className="text-right">{fmtEps(c.before_eps)}</TableCell>
                      <TableCell className="text-right">{fmtEps(c.after_eps)}</TableCell>
                      <TableCell className="text-right font-semibold">
                        <span className={Number(c.eps_delta_pct) > 0 ? "text-emerald-600" : "text-red-600"}>
                          {fmtPct(c.eps_delta_pct)}
                        </span>
                      </TableCell>
                      <TableCell className="space-x-1">
                        <SignalBadge signal={c.before_signal} />
                        <span className="text-muted-foreground">→</span>
                        <SignalBadge signal={c.after_signal} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Splits corrected */}
          {(today.splits_corrected || []).length > 0 && (
            <Alert data-testid="splits-corrected-alert">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Share splits auto-corrected today</AlertTitle>
              <AlertDescription>
                <ul className="list-disc pl-5 mt-1 space-y-0.5 text-sm">
                  {today.splits_corrected.map((s, i) => (
                    <li key={i}>
                      <Link to={`/stocks/${s.company_code}`} className="font-medium hover:underline">
                        {s.company_code}
                      </Link>
                      &nbsp;— ratio
                      <code className="mx-1 px-1 bg-muted rounded text-xs">
                        {Number(s.ratio || 0).toFixed(3)}
                      </code>
                      ({fmtNum(s.old_shares)} → {fmtNum(s.new_shares)})
                    </li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          {/* Failures */}
          {(today.failed || []).length > 0 && (
            <Alert variant="destructive" data-testid="failures-alert">
              <XCircle className="h-4 w-4" />
              <AlertTitle>OCR failures today ({today.failed.length})</AlertTitle>
              <AlertDescription>
                <ul className="list-disc pl-5 mt-1 space-y-0.5 text-sm">
                  {today.failed.slice(0, 10).map((f, i) => (
                    <li key={i}>
                      <Link to={`/stocks/${f.company_code}`} className="font-medium hover:underline">
                        {f.company_code}
                      </Link>
                      <span className="text-muted-foreground"> — {f.error || "unknown error"}</span>
                    </li>
                  ))}
                  {today.failed.length > 10 && (
                    <li className="text-muted-foreground">…and {today.failed.length - 10} more</li>
                  )}
                </ul>
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* ───────── SECTION 2 — Recent Stock Updates (v12.4: moved up)
          Bumped above Pipeline Status because admins want to see WHICH
          stocks just got updated immediately after a run, not after
          scrolling past coverage / scheduling metadata. ───────── */}
      <Card data-testid="section-recent-changes">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-emerald-600" /> Recent Stock Updates
          </CardTitle>
          <CardDescription>Last 20 stocks with new fundamentals.</CardDescription>
        </CardHeader>
        <CardContent>
          {recent.length === 0 ? (
            <div className="text-sm text-muted-foreground italic py-3">
              No recent changes. New updates appear here as the pipeline analyses fresh reports.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Stock</TableHead>
                  <TableHead className="text-right">Old EPS</TableHead>
                  <TableHead className="text-right">New EPS</TableHead>
                  <TableHead className="text-right">Change</TableHead>
                  <TableHead>Signal</TableHead>
                  <TableHead className="text-right">Date</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {recent.map((r, i) => (
                  <TableRow key={i} data-testid={`recent-change-${r.company_code}`}>
                    <TableCell className="font-medium">
                      <Link to={`/stocks/${r.company_code}`} className="hover:underline">
                        {r.company_code}
                      </Link>
                      {r.is_significant && (
                        <Badge variant="outline" className="ml-1 text-[10px] border-amber-400 text-amber-700">
                          significant
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">{fmtEps(r.old_eps)}</TableCell>
                    <TableCell className="text-right">{fmtEps(r.new_eps)}</TableCell>
                    <TableCell className="text-right">
                      <span className={
                        r.eps_delta_pct == null
                          ? "text-muted-foreground"
                          : Number(r.eps_delta_pct) > 0
                            ? "text-emerald-600 font-semibold"
                            : "text-red-600 font-semibold"
                      }>
                        {r.eps_delta_pct == null ? "—" : (
                          <>
                            {Number(r.eps_delta_pct) > 0
                              ? <TrendingUp className="inline h-3 w-3 mr-0.5" />
                              : <TrendingDown className="inline h-3 w-3 mr-0.5" />}
                            {fmtPct(r.eps_delta_pct)}
                          </>
                        )}
                      </span>
                    </TableCell>
                    <TableCell className="space-x-1">
                      <SignalBadge signal={r.signal_before} />
                      {r.signal_before !== r.signal_after && (
                        <>
                          <span className="text-muted-foreground">→</span>
                          <SignalBadge signal={r.signal_after} />
                        </>
                      )}
                    </TableCell>
                    <TableCell className="text-right text-xs text-muted-foreground">
                      {formatDateTime(r.date)}
                    </TableCell>
                    <TableCell>
                      <Link
                        to={`/stocks/${r.company_code}`}
                        className="text-muted-foreground hover:text-foreground"
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                      </Link>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* ───────── SECTION 2b — Failed Sanity (v5 2026-05-16) ─────────
          Reports whose OCR extraction was rejected by the PE / negative-EPS
          sanity gate. Almost always means Claude picked the wrong profit line
          (consolidated loss instead of parent-attributable, prior-year column,
          or unit error). Admin should open the company's Data Quality page
          and apply a manual override on the affected historical_fundamentals
          row, then recompute the score. */}
      <Card data-testid="section-failed-sanity" className={
        failedSanity.total > 0 ? "border-amber-300/60" : ""
      }>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <AlertTriangle className={`h-4 w-4 ${failedSanity.total > 0 ? "text-amber-600" : "text-muted-foreground"}`} />
            Failed Sanity Reports
            {failedSanity.total > 0 && (
              <Badge
                variant="outline"
                data-testid="failed-sanity-count"
                className="ml-1 border-amber-400 text-amber-900 dark:text-amber-200"
              >
                {failedSanity.total}
              </Badge>
            )}
          </CardTitle>
          <CardDescription>
            OCR extracted an impossible value (PE outside [2x, 200x] or per-share
            loss greater than half the share price). Open the company's Data
            Quality page, verify against CSEPal, and apply a manual override.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {failedSanity.rows.length === 0 ? (
            <div
              data-testid="failed-sanity-empty"
              className="text-sm text-muted-foreground italic py-3"
            >
              No reports flagged. The PE sanity gate is healthy — extractions
              are landing within plausible bands.
            </div>
          ) : (
            <Table data-testid="failed-sanity-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Stock</TableHead>
                  <TableHead>Period</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead className="text-right">Flagged</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {failedSanity.rows.map((r) => {
                  const firstDetail = (r.failed_sanity_details || [])[0] || {};
                  return (
                    <TableRow
                      key={r.report_id}
                      data-testid={`failed-sanity-row-${r.company_code}`}
                    >
                      <TableCell className="font-medium">
                        <Link
                          to={`/stocks/${r.company_code}`}
                          className="hover:underline"
                        >
                          {r.company_code}
                        </Link>
                      </TableCell>
                      <TableCell className="text-xs">
                        {r.report_period || "—"}
                        {r.report_type ? (
                          <span className="ml-1 text-muted-foreground">
                            ({r.report_type})
                          </span>
                        ) : null}
                      </TableCell>
                      <TableCell className="text-xs max-w-md">
                        <span className="text-amber-900 dark:text-amber-200">
                          {r.skip_reason || firstDetail.reason || "Sanity gate triggered"}
                        </span>
                        {firstDetail.financial_year && (
                          <div className="mt-0.5 text-muted-foreground">
                            FY {firstDetail.financial_year}
                            {firstDetail.eps !== undefined && firstDetail.eps !== null && (
                              <> · EPS {fmtEps(firstDetail.eps)}</>
                            )}
                            {firstDetail.pe !== undefined && firstDetail.pe !== null && (
                              <> · PE {Number(firstDetail.pe).toFixed(1)}x</>
                            )}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-right text-xs text-muted-foreground">
                        {formatDateTime(r.failed_sanity_at || r.analysed_at)}
                      </TableCell>
                      <TableCell>
                        <Link
                          to={`/admin/data-quality?company=${r.company_code}`}
                          className="text-muted-foreground hover:text-foreground"
                          data-testid={`failed-sanity-fix-${r.company_code}`}
                          title="Open Data Quality page to apply manual override"
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                        </Link>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* ───────── SECTION 3 — Pipeline Status ───────── */}
      <Card data-testid="section-pipeline-status">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Clock className="h-4 w-4 text-sky-600" /> Pipeline Status
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatTile
              label="Pending analysis"
              value={fmtNum(pipeline.pending)}
              tone={pipeline.pending > 0 ? "info" : "default"}
              testId="tile-pending"
            />
            <StatTile
              label="In progress"
              value={fmtNum(pipeline.in_progress)}
              tone={pipeline.in_progress > 0 ? "warning" : "default"}
              testId="tile-in-progress"
            />
            <div className="rounded-lg border border-border bg-card p-3" data-testid="tile-last-run">
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                Last run
              </div>
              {pipeline.last_run ? (
                <>
                  <div className="text-sm font-semibold">
                    {formatDateTime(pipeline.last_run.completed_at)}
                  </div>
                  <div className="text-[11px] text-muted-foreground">
                    {pipeline.last_run.triggered_by} · analysed {pipeline.last_run.analysed} · failed {pipeline.last_run.failed}
                  </div>
                </>
              ) : (
                <div className="text-sm text-muted-foreground">No runs yet</div>
              )}
            </div>
            <div className="rounded-lg border border-border bg-card p-3" data-testid="tile-next-run">
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                Scheduled runs
              </div>
              <div className="text-[11px] leading-tight space-y-0.5">
                <div>📥 {pipeline.next_runs?.download_check}</div>
                <div>🔁 {pipeline.next_runs?.analyse_safety_net}</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ───────── SECTION 4 — Coverage ───────── */}
      <Card data-testid="section-coverage">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Database className="h-4 w-4 text-indigo-600" /> Data Coverage
          </CardTitle>
          <CardDescription>
            Across {fmtNum(coverage.total_active)} active companies. Buckets
            use the report's <strong>period_end_date</strong> (financial
            year-end), not the OCR timestamp.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatTile
              label="Recent (≤ 18 mo)"
              value={fmtNum(coverage.with_recent_data ?? coverage.with_recent_data_3mo)}
              icon={CheckCircle2}
              tone="success"
              testId="tile-coverage-recent"
            />
            <StatTile
              label="Old (18–36 mo)"
              value={fmtNum(coverage.with_old_data ?? coverage.with_old_data_6mo)}
              icon={Clock}
              tone={(coverage.with_old_data ?? coverage.with_old_data_6mo) > 0 ? "warning" : "default"}
              testId="tile-coverage-old"
            />
            <StatTile
              label="No annual data"
              value={fmtNum(coverage.no_data)}
              icon={XCircle}
              tone={coverage.no_data > 0 ? "danger" : "default"}
              testId="tile-coverage-none"
            />
            <StatTile
              label="Total active"
              value={fmtNum(coverage.total_active)}
              icon={Database}
              testId="tile-coverage-total"
            />
          </div>

          {/* Data source breakdown — 12.1 addition */}
          {coverage.data_source_breakdown && (
            <div data-testid="coverage-data-source-breakdown">
              <div className="text-xs font-medium text-muted-foreground mb-2">
                Data source breakdown
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatTile
                  label="Annual report"
                  value={fmtNum(coverage.data_source_breakdown.annual_report || 0)}
                  icon={CheckCircle2}
                  tone="success"
                  testId="tile-source-annual"
                />
                <StatTile
                  label="TradingView"
                  value={fmtNum(coverage.data_source_breakdown.tradingview || 0)}
                  icon={Database}
                  tone={(coverage.data_source_breakdown.tradingview || 0) > 0 ? "info" : "default"}
                  testId="tile-source-tv"
                />
                <StatTile
                  label="Manual"
                  value={fmtNum(coverage.data_source_breakdown.manual || 0)}
                  icon={Database}
                  testId="tile-source-manual"
                />
                <StatTile
                  label="None"
                  value={fmtNum(coverage.data_source_breakdown.none || 0)}
                  icon={XCircle}
                  tone={(coverage.data_source_breakdown.none || 0) > 0 ? "danger" : "default"}
                  testId="tile-source-none"
                />
              </div>
            </div>
          )}

          {/* Phase 12.6 — Backfill coverage. Surfaces companies that
              still need their latest annual / post-Apr-2026 interim
              downloaded so admins can see backfill progress shrink
              over the daily ticks. */}
          {coverage.backfill && (
            <BackfillCoverageBlock
              backfill={coverage.backfill}
              backfillState={backfillState}
              onRunBackfill={handleRunBackfill}
              starting={backfillStarting}
            />
          )}
        </CardContent>
      </Card>

      {/* ───────── SECTION 5 — v13.0 Title-FY Remediation ─────────
          Two diagnostics:
          A. Recompute Periods — backfill `report_period` from the title
             (fixes LGIL Dec 2025 → Dec 2024 etc.)
          B. CSE Has No Annual — list active companies CSE itself has
             no annual report for (CALU, INME, WLTH, NEST, …). */}
      <Card data-testid="section-title-fy-remediation">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            Title-FY Remediation
            <Badge variant="outline" className="ml-1 text-[10px]">v13.0</Badge>
          </CardTitle>
          <CardDescription>
            Fixes for mis-labeled annual reports (e.g. LGIL stored as
            "Dec 2025" when title says "Annual Report 2024") and a
            diagnostic for stocks where CSE itself hosts zero annual
            PDFs (CALU, INME, WLTH, NEST).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* ─── A. Recompute Periods ─── */}
          <div className="space-y-3">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="space-y-1">
                <h3 className="text-sm font-semibold flex items-center gap-2">
                  <Database className="h-4 w-4 text-indigo-600" />
                  Recompute report_period from title
                </h3>
                <p className="text-xs text-muted-foreground max-w-2xl">
                  Re-derives <code className="text-[11px] bg-muted px-1 rounded">report_period</code> from the
                  report title (most authoritative), falling back to CSE's <code className="text-[11px] bg-muted px-1 rounded">manualDate</code> only
                  when the title gives no signal. Fixes typos and publication-date confusion.
                  Each row's previous label is preserved as <code className="text-[11px] bg-muted px-1 rounded">previous_report_period</code>.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={fetchRecomputePreview}
                  disabled={recomputeRunning}
                  data-testid="recompute-preview-btn"
                >
                  {recomputeRunning ? (
                    <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Working…</>
                  ) : (
                    <><RefreshCcw className="h-4 w-4 mr-1" /> Preview</>
                  )}
                </Button>
                <Button
                  size="sm"
                  onClick={handleApplyRecompute}
                  disabled={recomputeRunning || !recomputePreview || recomputePreview.would_update === 0}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  data-testid="recompute-apply-btn"
                >
                  {recomputeRunning ? (
                    <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Applying…</>
                  ) : (
                    <>Apply {recomputePreview?.would_update ? `(${recomputePreview.would_update})` : ""}</>
                  )}
                </Button>
              </div>
            </div>
            {recomputePreview && (
              <div className="rounded-lg border bg-muted/30 p-3 space-y-2" data-testid="recompute-preview-result">
                <div className="text-xs flex flex-wrap gap-4">
                  <span><strong>{recomputePreview.dry_run ? "Would update" : "Updated"}:</strong> {recomputePreview.dry_run ? recomputePreview.would_update : recomputePreview.applied}</span>
                  <span><strong>Kept unchanged:</strong> {recomputePreview.kept_unchanged}</span>
                  {!recomputePreview.dry_run && (
                    <Badge variant="outline" className="text-[10px] border-emerald-400 text-emerald-700">
                      <CheckCircle2 className="h-3 w-3 mr-1" /> Applied
                    </Badge>
                  )}
                </div>
                {recomputePreview.samples?.length > 0 && (
                  <div className="overflow-x-auto rounded border bg-background">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-xs">Stock</TableHead>
                          <TableHead className="text-xs">Old period</TableHead>
                          <TableHead className="text-xs">→ New period</TableHead>
                          <TableHead className="text-xs">Source</TableHead>
                          <TableHead className="text-xs">Title</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {recomputePreview.samples.slice(0, 15).map((s, i) => (
                          <TableRow key={i} data-testid={`recompute-sample-${i}`}>
                            <TableCell className="text-xs font-mono">{s.company_code}</TableCell>
                            <TableCell className="text-xs">
                              <Badge variant="outline" className="text-[10px]">{s.old_period || "—"}</Badge>
                            </TableCell>
                            <TableCell className="text-xs">
                              <Badge className="text-[10px] bg-emerald-100 text-emerald-900 hover:bg-emerald-100">
                                {s.new_period}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-[11px] text-muted-foreground font-mono">{s.source}</TableCell>
                            <TableCell className="text-xs max-w-md truncate" title={s.title}>{s.title}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ─── B. CSE Has No Annual ─── */}
          <div className="space-y-3 border-t pt-4">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="space-y-1">
                <h3 className="text-sm font-semibold flex items-center gap-2">
                  <XCircle className="h-4 w-4 text-rose-600" />
                  Active companies where CSE has no annual
                </h3>
                <p className="text-xs text-muted-foreground max-w-2xl">
                  These stocks appear as "missing FY2024/FY2025" in every backfill sweep,
                  but the bug isn't ours — CSE itself hosts zero annual PDFs for them
                  (newly-listed issuers, closed-end funds, delisted issuers etc.).
                  Result is cached for 1 hour.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchCseNoAnnual(false)}
                  disabled={cseNoAnnualLoading}
                  data-testid="cse-no-annual-load-btn"
                >
                  {cseNoAnnualLoading ? (
                    <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Probing…</>
                  ) : (
                    <><Database className="h-4 w-4 mr-1" /> Load</>
                  )}
                </Button>
                {cseNoAnnual && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => fetchCseNoAnnual(true)}
                    disabled={cseNoAnnualLoading}
                    data-testid="cse-no-annual-refresh-btn"
                  >
                    <RefreshCcw className={`h-4 w-4 mr-1 ${cseNoAnnualLoading ? "animate-spin" : ""}`} />
                    Force refresh
                  </Button>
                )}
              </div>
            </div>
            {cseNoAnnual && (
              <div className="rounded-lg border bg-muted/30 p-3 space-y-2" data-testid="cse-no-annual-result">
                {/* Probe status row. Shown while running + after done. */}
                <div className="text-xs flex flex-wrap gap-4 items-center">
                  <Badge
                    variant="outline"
                    className={
                      cseNoAnnual.status === "running" ? "border-amber-400 text-amber-700" :
                      cseNoAnnual.status === "done"    ? "border-emerald-400 text-emerald-700" :
                      cseNoAnnual.status === "error"   ? "border-rose-400 text-rose-700" :
                      "border-zinc-300 text-zinc-700"
                    }
                  >
                    {cseNoAnnual.status === "running" && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
                    {cseNoAnnual.status === "done"    && <CheckCircle2 className="h-3 w-3 mr-1" />}
                    {cseNoAnnual.status === "error"   && <XCircle className="h-3 w-3 mr-1" />}
                    {cseNoAnnual.status}
                  </Badge>
                  {cseNoAnnual.status === "running" && cseNoAnnual.total > 0 && (
                    <span>
                      Probing CSE: <strong>{cseNoAnnual.checked}</strong> / {cseNoAnnual.total}
                      ({Math.round((cseNoAnnual.progress || 0) * 100)}%)
                    </span>
                  )}
                  {cseNoAnnual.error && (
                    <span className="text-rose-700">{cseNoAnnual.error}</span>
                  )}
                </div>

                {cseNoAnnual.status === "running" && (
                  <Progress
                    value={Math.round((cseNoAnnual.progress || 0) * 100)}
                    className="h-1.5"
                  />
                )}

                {cseNoAnnual.result && (
                  <>
                    <div className="text-xs flex flex-wrap gap-4">
                      <span><strong>Checked:</strong> {cseNoAnnual.result.active_companies_checked}</span>
                      <span><strong>No-annual count:</strong>
                        <Badge variant="outline" className="ml-1 text-[10px] border-rose-400 text-rose-700">
                          {cseNoAnnual.result.no_annual_count}
                        </Badge>
                      </span>
                      {cseNoAnnual.result.errors?.length > 0 && (
                        <span><strong>Probe errors:</strong> {cseNoAnnual.result.errors.length}</span>
                      )}
                      <span className="text-muted-foreground">
                        {cseNoAnnual.result.checked_at && `Probed at ${formatDateTime(cseNoAnnual.result.checked_at)}`}
                      </span>
                    </div>
                    {cseNoAnnual.result.no_annual?.length > 0 ? (
                      <div className="overflow-x-auto rounded border bg-background">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="text-xs">Stock</TableHead>
                              <TableHead className="text-xs">Name</TableHead>
                              <TableHead className="text-xs">CSE symbol</TableHead>
                              <TableHead className="text-xs text-right">Total rows on CSE</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {cseNoAnnual.result.no_annual.map((c) => (
                              <TableRow key={c.company_code} data-testid={`cse-no-annual-row-${c.company_code}`}>
                                <TableCell className="text-xs font-mono">{c.company_code}</TableCell>
                                <TableCell className="text-xs max-w-xs truncate" title={c.company_name}>
                                  {c.company_name}
                                </TableCell>
                                <TableCell className="text-xs font-mono text-muted-foreground">{c.cse_symbol}</TableCell>
                                <TableCell className="text-xs text-right">{c.total_rows}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    ) : (
                      <p className="text-xs text-emerald-700 dark:text-emerald-400 flex items-center gap-1">
                        <CheckCircle2 className="h-4 w-4" /> Every active stock has at least one annual on CSE.
                      </p>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <p className="text-xs text-muted-foreground text-center pt-2">
        Auto-refreshes every 30s (10s while a run is in flight) ·
        generated {formatDateTime(status?.generated_at)}
      </p>
    </div>
  );
};

export default AdminReportPipelinePage;
